import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { loadTurkishFont, TURKISH_FONT } from '../../../shared/utils/pdfTurkishFont';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatDateForFile = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const sanitizeFilePart = (value) => (value || '')
  .toString()
  .trim()
  .replace(/\s+/g, '_')
  .replace(/[^\w.-]/g, '')
  .slice(0, 48);

const formatDateTime = (language = 'tr-TR') => new Date().toLocaleString(language);

const COVER_TEXT = {
  'tr-TR': {
    reportTitle: 'AI Mali Analiz ve Karar Destek Raporu',
    reportSubtitle: 'CFO Sunum Paketi',
    company: 'Sirket',
    period: 'Donem',
    generatedAt: 'Olusturma Zamani',
    confidentiality: 'Gizli - Sadece Yetkili Yonetim Kullanimi Icindir',
    sectionTitle: 'Yonetici Ozeti',
    sectionBody: 'Bu dokuman, AI destekli finansal analiz, kritik sapmalar ve aksiyon onceliklerini yonetici seviyesi sunum formatinda bir araya getirir.'
  },
  'en-US': {
    reportTitle: 'AI Financial Analysis and Decision Support Report',
    reportSubtitle: 'CFO Presentation Pack',
    company: 'Company',
    period: 'Period',
    generatedAt: 'Generated At',
    confidentiality: 'Confidential - For Authorized Management Use Only',
    sectionTitle: 'Executive Summary',
    sectionBody: 'This document consolidates AI-powered financial analysis, critical deviations and action priorities in an executive presentation format.'
  }
};

const t = (language, key) => COVER_TEXT[language]?.[key] || COVER_TEXT['tr-TR'][key] || key;

// Selectors that mark natural section boundaries for smart page breaking
const BREAK_SELECTORS = [
  '.decision-kpi-grid',
  '.ai-snapshot-row',
  '.analysis-section',
  '.ai-saved-reports-card',
  '.ai-financial-report-meta',
  '.ai-financial-report-body',
].join(', ');

const getDocumentTop = (el) => {
  let top = 0;
  let node = el;
  while (node) {
    top += node.offsetTop;
    node = node.offsetParent;
  }
  return top;
};

const createCanvasSlice = (sourceCanvas, startPx, endPx) => {
  const safeStart = Math.max(0, Math.floor(startPx));
  const safeEnd = Math.min(sourceCanvas.height, Math.ceil(endPx));
  const sliceHeight = Math.max(1, safeEnd - safeStart);
  const sliceCanvas = document.createElement('canvas');
  sliceCanvas.width = sourceCanvas.width;
  sliceCanvas.height = sliceHeight;
  const ctx = sliceCanvas.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, safeStart, sourceCanvas.width, sliceHeight, 0, 0, sourceCanvas.width, sliceHeight);
  return sliceCanvas;
};

export async function exportAiReportPdf({
  element,
  companyName = '',
  periodLabel = '',
  language = 'tr-TR'
}) {
  if (!element) {
    throw new Error(language.startsWith('tr') ? 'PDF alani bulunamadi.' : 'PDF source area not found.');
  }

  // Step 1: Temporarily expand overflow containers in the real DOM to measure
  // the full scrollHeight that includes the markdown report content.
  // html2canvas receives `height` before onclone runs, so we must pre-measure.
  const mdElements = Array.from(element.querySelectorAll('.ai-report-md'));
  const savedMdStyles = mdElements.map((el) => ({
    maxHeight: el.style.maxHeight,
    overflow: el.style.overflow,
    height: el.style.height,
  }));
  mdElements.forEach((el) => {
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
    el.style.height = 'auto';
  });
  const fullScrollHeight = element.scrollHeight;
  // Restore immediately — the actual expansion happens again inside onclone.
  mdElements.forEach((el, i) => {
    el.style.maxHeight = savedMdStyles[i].maxHeight;
    el.style.overflow = savedMdStyles[i].overflow;
    el.style.height = savedMdStyles[i].height;
  });

  // Step 2: Collect section break points from DOM (after restore, layout is back to normal).
  const elementDocTop = getDocumentTop(element);
  const rawBreaksDom = [];
  element.querySelectorAll(BREAK_SELECTORS).forEach((el) => {
    const relTop = getDocumentTop(el) - elementDocTop;
    if (relTop > 20) rawBreaksDom.push(relTop);
  });

  const scale = clamp(window.devicePixelRatio || 1, 1.5, 2.5);
  const canvas = await html2canvas(element, {
    backgroundColor: '#0f1b33',
    scale,
    useCORS: true,
    allowTaint: false,
    logging: false,
    width: element.scrollWidth,
    height: fullScrollHeight,
    windowWidth: Math.max(element.scrollWidth, window.innerWidth),
    windowHeight: Math.max(fullScrollHeight, window.innerHeight),
    scrollX: 0,
    scrollY: -window.scrollY,
    onclone: (clonedDocument) => {
      clonedDocument.querySelectorAll('[data-export-hidden="true"]').forEach((node) => node.remove());
      // Expand markdown container in the clone so the full content renders into the canvas.
      clonedDocument.querySelectorAll('.ai-report-md').forEach((el) => {
        el.style.maxHeight = 'none';
        el.style.overflow = 'visible';
        el.style.height = 'auto';
      });
    }
  });

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const nowLabel = formatDateTime(language);

  // ── Cover page ────────────────────────────────────────────────────────────
  doc.setFillColor(11, 18, 32);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  doc.setFillColor(22, 37, 64);
  doc.roundedRect(10, 10, pageWidth - 20, pageHeight - 20, 4, 4, 'F');

  doc.setFillColor(39, 72, 130);
  doc.rect(10, 10, pageWidth - 20, 22, 'F');

  doc.setTextColor(240, 246, 255);
  doc.setFont(TURKISH_FONT, 'bold');
  doc.setFontSize(20);
  doc.text(t(language, 'reportTitle'), 16, 24);
  doc.setFontSize(11);
  doc.setFont(TURKISH_FONT, 'normal');
  doc.text(t(language, 'reportSubtitle'), 16, 30);

  doc.setDrawColor(73, 119, 190);
  doc.setLineWidth(0.6);
  doc.line(16, 42, pageWidth - 16, 42);

  doc.setTextColor(214, 228, 255);
  doc.setFont(TURKISH_FONT, 'bold');
  doc.setFontSize(12);
  doc.text(t(language, 'sectionTitle'), 16, 52);
  doc.setFont(TURKISH_FONT, 'normal');
  doc.setFontSize(10.5);
  doc.text(doc.splitTextToSize(t(language, 'sectionBody'), 110), 16, 59);

  doc.setFillColor(19, 32, 56);
  doc.roundedRect(pageWidth - 92, 48, 76, 55, 3, 3, 'F');
  doc.setDrawColor(54, 87, 145);
  doc.roundedRect(pageWidth - 92, 48, 76, 55, 3, 3, 'S');

  doc.setTextColor(232, 241, 255);
  doc.setFont(TURKISH_FONT, 'bold');
  doc.setFontSize(10);
  doc.text(`${t(language, 'company')}:`, pageWidth - 88, 58);
  doc.text(`${t(language, 'period')}:`, pageWidth - 88, 72);
  doc.text(`${t(language, 'generatedAt')}:`, pageWidth - 88, 86);

  doc.setFont(TURKISH_FONT, 'normal');
  doc.setFontSize(9.5);
  doc.text(doc.splitTextToSize(companyName || '-', 52), pageWidth - 88, 63);
  doc.text(doc.splitTextToSize(periodLabel || '-', 52), pageWidth - 88, 77);
  doc.text(doc.splitTextToSize(nowLabel, 52), pageWidth - 88, 91);

  doc.setTextColor(164, 185, 222);
  doc.setFontSize(8.8);
  doc.text(t(language, 'confidentiality'), 16, pageHeight - 14);

  // ── Content pages ─────────────────────────────────────────────────────────
  const margin = 6;
  const renderWidth = pageWidth - margin * 2;
  const renderHeight = (canvas.height * renderWidth) / canvas.width;
  const printableHeight = pageHeight - margin * 2;

  // Convert collected DOM break positions to PDF mm coordinates.
  // effectiveDomHeight is the full height html2canvas actually rendered (may differ
  // from element.scrollHeight after the overflow expansion in onclone).
  const effectiveDomHeight = canvas.height / scale;
  const breaksMm = rawBreaksDom
    .map((y) => (y / effectiveDomHeight) * renderHeight)
    .sort((a, b) => a - b);

  // Find the last section boundary that fits within the current page.
  // Strategy: scan backwards from the natural page end to find the last break point
  // that is at least 30% into the page (to guarantee forward progress).
  // This ensures cuts always happen between sections, never mid-section.
  const findSmartBreak = (currentOffset, targetMm) => {
    const minBreak = currentOffset + printableHeight * 0.3;
    let best = null;
    for (const bp of breaksMm) {
      if (bp >= minBreak && bp <= targetMm) {
        best = bp; // keep overwriting — we want the LAST one before targetMm
      }
    }
    return best !== null ? best : targetMm;
  };

  const pageSegments = [];
  let offsetY = 0;
  while (offsetY < renderHeight - 0.5) {
    const nextOffset = findSmartBreak(offsetY, Math.min(offsetY + printableHeight, renderHeight));
    const resolvedOffset = nextOffset > offsetY ? nextOffset : Math.min(offsetY + printableHeight, renderHeight);
    pageSegments.push([offsetY, resolvedOffset]);
    offsetY = resolvedOffset;
  }

  pageSegments.forEach(([startMm, endMm]) => {
    doc.addPage('a4', 'landscape');
    doc.setFillColor(11, 18, 32);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    const startPx = (startMm / renderHeight) * canvas.height;
    const endPx = (endMm / renderHeight) * canvas.height;
    const sliceCanvas = createCanvasSlice(canvas, startPx, endPx);
    const sliceHeightMm = endMm - startMm;

    doc.addImage(
      sliceCanvas.toDataURL('image/png', 1),
      'PNG',
      margin,
      margin,
      renderWidth,
      sliceHeightMm,
      undefined,
      'FAST'
    );
  });

  // ── Save ──────────────────────────────────────────────────────────────────
  const safeCompany = sanitizeFilePart(companyName);
  const safePeriod = sanitizeFilePart(periodLabel);
  const safeTitle = sanitizeFilePart(t(language, 'reportTitle')) || 'ai_financial_report';
  const datePart = formatDateForFile(new Date());
  const filenameParts = [safeTitle, safeCompany, safePeriod, datePart].filter(Boolean);
  doc.save(`${filenameParts.join('_')}.pdf`);
}
